import { getSessionAuthentication, baseUrl, rms2BasePath } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier,
    };
}

async function rms2Fetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T> {
    const response = await fetch(rms2BasePath + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body,
    });
    if (!response.ok) {
        const text = await response.text();
        let message = `API error: ${response.status} ${response.statusText}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed?.message) message = parsed.message;
        } catch { /* ignore */ }
        throw new Error(message);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

async function legacyFetch<T>(path: string): Promise<T> {
    const response = await fetch(baseUrl + path, {
        method: 'GET',
        headers: getHeaders(),
    });
    if (!response.ok) {
        const text = await response.text();
        let message = `API error: ${response.status} ${response.statusText}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed?.message) message = parsed.message;
        } catch { /* ignore */ }
        throw new Error(message);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

export interface Language {
    id: number;
    name: string;
    locale?: string;
}

export interface Localization {
    language: Language;
    name?: string;
}

export interface PrintDocumentTemplate {
    id: number;
    description: string;
}

export interface DeliveryPlanningTemplate {
    id: number;
    deliveryPlanningTemplateName: string;
}

export interface OrderType {
    id: number;
    orderTypeName: string;
    printDocumentTemplate: PrintDocumentTemplate | null;
    pickingListDocumentTemplate: PrintDocumentTemplate | null;
    deliveryPlanningTemplate: DeliveryPlanningTemplate | null;
    deliveryPlanningTemplateId?: number | null;
    localizations: Localization[];
}

export async function getOrderType(id: number): Promise<OrderType> {
    return rms2Fetch<OrderType>(`ordertypes/${id}`);
}

export async function createOrderType(ot: Partial<OrderType>): Promise<OrderType> {
    return rms2Fetch<OrderType>(`ordertypes/${ot.id}`, {
        method: 'PUT',
        body: JSON.stringify({
            orderTypeName: ot.orderTypeName,
            printDocumentTemplateId: ot.printDocumentTemplate?.id ?? null,
            pickingListDocumentTemplateId: ot.pickingListDocumentTemplate?.id ?? null,
            deliveryPlanningTemplateId: ot.deliveryPlanningTemplateId ?? null,
            localizations: ot.localizations?.map(l => ({
                languageId: l.language.id,
                name: l.name,
            })) ?? [],
        }),
    });
}

export async function updateOrderType(ot: OrderType): Promise<OrderType> {
    return rms2Fetch<OrderType>(`ordertypes/${ot.id}`, {
        method: 'PUT',
        body: JSON.stringify({
            orderTypeName: ot.orderTypeName,
            printDocumentTemplateId: ot.printDocumentTemplate?.id ?? null,
            pickingListDocumentTemplateId: ot.pickingListDocumentTemplate?.id ?? null,
            deliveryPlanningTemplateId: ot.deliveryPlanningTemplateId ?? null,
            localizations: ot.localizations?.map(l => ({
                languageId: l.language.id,
                name: l.name,
            })) ?? [],
        }),
    });
}

export async function deleteOrderType(id: number): Promise<void> {
    throw new Error('Delete not supported for order types');
}

export async function getLanguages(): Promise<Language[]> {
    return rms2Fetch<{ languages: Language[] }>('languages')
        .then(r => r.languages);
}

export async function getPrintDocumentTemplates(): Promise<PrintDocumentTemplate[]> {
    return rms2Fetch<{ printDocumentTemplates: PrintDocumentTemplate[] }>('ordertypes/printDocumentTemplates')
        .then(r => r.printDocumentTemplates);
}

export async function getDeliveryPlanningTemplates(): Promise<{ deliveryPlanningTemplates: DeliveryPlanningTemplate[] }> {
    return rms2Fetch<{ deliveryPlanningTemplates: DeliveryPlanningTemplate[] }>('ordertypes/deliveryPlanningTemplates');
}

// This endpoint is on the old orders module — keep on legacyFetch
export async function isDeliveryPlanningEnabled(): Promise<boolean> {
    return legacyFetch<boolean>('orders/isdeliveryplanningenabled');
}
